import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { IUserBillingInfo, IUserSessionData } from 'app/store/models/user.model';
import { Subject } from 'rxjs';
import { SessionService } from 'app/store/services/session.service';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { ISelectItem } from 'app/store/models/select.model';
import { UtilitiesService } from 'app/store/services/utilities.servive';
import { UserService } from 'app/store/services/user.service';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { openSnackbar } from '../../store/actions/page.action';
import { getUserSession } from '../../store/actions/user.action';
import { TimezonePipe } from '../../shared/ui/pipes/timezone.pipe';
import { DialogService } from '../../shared/ui/dialog/dialog.service';
import { LoadingSpinnerComponent } from '../../dialogs/loading-spinner/loading-spinner.component';
import { DiscountsService } from '../../store/services/discounts.service';
import { IPlanProduct } from '../../store/models/discount.model';
import { Router } from '@angular/router';

@Component({
  selector: 'app-billing-information',
  templateUrl: './billing-information.component.html',
  styleUrls: ['./billing-information.component.scss'],
})
export class BillingInformationComponent implements OnInit, OnDestroy {
  formGroup: FormGroup;

  cancelButtonProperties: Button = {
    text: 'Cancel',
    color: ButtonColor.RED,
    size: ButtonSize.NORMAL,
    route: ['/messages'],
    style: 'outline',
  };

  continueButtonProperties: Button = {
    text: 'Save',
    color: ButtonColor.ORANGE,
    size: ButtonSize.NORMAL,
    onClick: () => this.submitForm(),
  };
  disableContinueButton: boolean;

  months: ISelectItem[] = [];
  currentMonth: number;

  years: ISelectItem[] = [];
  currentYear: number;

  sessionData: IUserSessionData;

  countries: ISelectItem[];
  states: ISelectItem[];
  currentCountry = '';

  updateCardNumber: boolean;

  billingInfo: IUserBillingInfo;

  accountId: number;

  formReady = false;

  showPlanSelection: boolean;
  plans: IPlanProduct[] = [];
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private sessionService: SessionService,
    private utilService: UtilitiesService,
    private userService: UserService,
    private readonly store$: Store<IReducers>,
    private readonly dialogService: DialogService,
    private readonly discountService: DiscountsService,
    private readonly router: Router,
  ) {
  }

  get expire_month() {
    return this.formGroup.get('expire_month');
  }

  get expire_year() {
    return this.formGroup.get('expire_year');
  }

  get planItems(): ISelectItem[] {
    return this.plans.map(p => ({
      label: p.name,
      value: p.plan_id,
    }));
  }

  get impersonating(): boolean {
    return this.sessionService.isImpersonating();
  }

  get canUpdateInformation(): boolean {
    return true;
    // return this.sessionData.billing_status !== 'Active';
  }

  submitForm() {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    const form = this.formGroup.getRawValue();
    if (this.sessionData.transaction_id) {
      delete form.team_code;
    }

    const dialogRef = this.dialogService.openDialog({
      title: 'Updating your payment details...',
      content: LoadingSpinnerComponent,
      canClose: false,
    });

    form.card_expiration_date = `${form.expire_year}-${form.expire_month}-01`;
    form.card_id = form.cvc_code;
    delete form.team_code;
    delete form.cvc_code;
    this.userService.updateAccountPayment(this.accountId, form).subscribe(() => {
      dialogRef.close();
      this.store$.dispatch(getUserSession());
      this.store$.dispatch(
        openSnackbar({
          snackbarData: {
            data: {
              type: 'success',
              message: 'Information was successfully updated',
            },
            verticalPosition: 'bottom',
          },
        }),
      );
      this.router.navigate(['/messages']);
    }, error => {
      dialogRef.close();
      this.store$.dispatch(openSnackbar({
        snackbarData: {
          data: {
            type: 'error',
            message: error.error,
          },
        },
      }));
    });
  }

  getYears() {
    const currentYear = moment().year();
    const result = [currentYear];
    for (let i = currentYear; i < currentYear + 16; i++) {
      result.push(i);
    }

    return result;
  }

  getCardNumber(): string {
    return this.billingInfo.card_number
      .toString()
      .substring(this.billingInfo.card_number.toString().length - 4);
  }

  onUpdateCardNumber(value: boolean) {
    this.updateCardNumber = !value;
  }

  ngOnInit() {
    this.billingInfo = {
      card_number: 411111111111,
    };
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.sessionData = sessionData;
        this.disableContinueButton = !this.canUpdateInformation;
        this.accountId = sessionData.id;

        const months = moment.months();
        for (let i = 0; i < months.length; i++) {
          this.months.push({
            label: months[i],
            value: i + 1,
          });
        }
        this.currentMonth = moment().month() + 1;

        const years = this.getYears();
        for (const i of years) {
          this.years.push({
            label: i.toString(),
            value: i,
          });
        }
        this.currentYear = moment().year();

        this.countries = Object.keys(this.utilService.getCountries()).map(
          country => {
            return {
              label: this.utilService.getCountries()[country],
              value: country,
            };
          },
        );

        this.userService.getBillingInfo(sessionData.id).subscribe(response => {
          this.billingInfo = response;
          const pipe = new TimezonePipe(this.utilService);

          const expMonth = this.billingInfo.card_expiration_date
            ? pipe.transform(this.billingInfo.card_expiration_date, 'M')
            : moment().month() + 1;
          const expYear = this.billingInfo.card_expiration_date
            ? pipe.transform(this.billingInfo.card_expiration_date, 'yyyy')
            : moment().year();
          this.currentCountry = this.billingInfo.country;

          this.formGroup = new FormGroup({
            card_name: new FormControl(
              this.billingInfo.name_on_card
                ? this.billingInfo.name_on_card
                : '',
              [Validators.required],
            ),
            card_number: new FormControl(
              this.billingInfo.card_number
                ? this.billingInfo.card_number
                : '',
              [Validators.required],
            ),
            expire_month: new FormControl(+expMonth, [Validators.required]),
            expire_year: new FormControl(+expYear, [Validators.required]),
            cvc_code: new FormControl(
              this.billingInfo.cvc_code
                ? this.billingInfo.cvc_code
                : '',
              [
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(4),
                Validators.pattern(/^[0-9]*$/),
              ],
            ),

            team_code: new FormControl(''),
          });

          if (this.sessionData.billing_status === 'Missing registration') {
            this.discountService.getPlanProducts()
              .subscribe(data => {
                if (data && data.items && data.items.length) {
                  this.plans = data.items;
                  this.formGroup.addControl('plan_id', new FormControl('', [Validators.required]));
                  this.showPlanSelection = true;
                  this.formReady = true;
                }
              });
          } else {
            this.showPlanSelection = false;
            this.formReady = true;
          }


          this.formGroup.patchValue({
            expire_month: +expMonth,
            expire_year: +expYear,
            state: this.billingInfo.state,
          });
        });
      });
  }

  ngOnDestroy() {
    this.destroy$.next(false);
  }

  getMaskedValue(currentValue: string): string {
    return Array.from(currentValue).map(v => '*').join('');
  }
}
